import React, { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Popper } from '@fragments';
import { IconChevronRight, IconDots } from '@tabler/icons-react';
import './styles.scss';

// ── Single crumb ─────────────────────────────────────────────────────────────
// Ancestors render as a muted, clickable label that brightens to full contrast
// on hover; the current crumb renders strong and inert with aria-current="page".
function Crumb({ item, isCurrent }) {
    const { label, href, onClick, icon, disabled } = item;

    const handleActivate = (event) => {
        if (onClick) {
            event.preventDefault();
            onClick(event);
        }
        // No onClick + an href: let the native <a> navigation proceed.
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleActivate(event);
        }
    };

    // Current crumb — strong contrast, not interactive.
    if (isCurrent) {
        return (
            <Text.label
                contrast={9}
                left={icon}
                aria-current="page"
                className="breadcrumb__crumb breadcrumb__crumb--current"
            >
                {label}
            </Text.label>
        );
    }

    // Disabled crumb — a step that can't be reached yet: faintest ink, inert.
    if (disabled) {
        return (
            <Text.label
                contrast={5}
                left={icon}
                aria-disabled="true"
                className="breadcrumb__crumb breadcrumb__crumb--disabled"
            >
                {label}
            </Text.label>
        );
    }

    // Ancestor with href and no onClick: render a real anchor for native nav.
    if (href && !onClick) {
        return (
            <a
                href={href}
                className="breadcrumb__link"
                onKeyDown={handleKeyDown}
            >
                <Text.label
                    contrast={6}
                    left={icon}
                    className="breadcrumb__crumb breadcrumb__crumb--link"
                >
                    {label}
                </Text.label>
            </a>
        );
    }

    // Ancestor with onClick (or neither): keyboard-accessible button role.
    return (
        <Text.label
            contrast={6}
            left={icon}
            role="button"
            tabIndex={0}
            onClick={handleActivate}
            onKeyDown={handleKeyDown}
            className="breadcrumb__crumb breadcrumb__crumb--link"
        >
            {label}
        </Text.label>
    );
}

// ── Separator ────────────────────────────────────────────────────────────────
function Separator({ separator }) {
    return (
        <span className="breadcrumb__separator" aria-hidden="true">
            {separator != null
                ? separator
                : <IconChevronRight size={14} stroke={2} />}
        </span>
    );
}

/**
 * Breadcrumb — a horizontal path/navigation trail.
 *
 * @param {Array<{label, href?, onClick?, icon?, current?}>} items
 * @param {ReactNode} [separator]  Override the default chevron (e.g. a "/" string).
 * @param {number}    [maxItems=4] Collapse the middle into a "…" menu past this count.
 */
export default function Breadcrumb({
    items = [],
    separator,
    maxItems = 4,
    className = '',
    ...props
}) {
    const { t } = useTranslation('fragments');
    const popperRef = useRef(null);

    // Resolve which index is "current": an explicit current:true wins, otherwise
    // the last item is treated as the current page.
    const currentIndex = useMemo(() => {
        const explicit = items.findIndex((it) => it?.current);
        return explicit !== -1 ? explicit : items.length - 1;
    }, [items]);

    if (!items.length) return null;

    const isCurrent = (index) => index === currentIndex;

    // Decide whether to collapse: only when there are genuinely hidden middle
    // crumbs (keep first + last visible). maxItems must leave room for both ends.
    const shouldCollapse = items.length > maxItems && maxItems >= 2;

    // ── Collapsed: first + … + last ──────────────────────────────────────────
    if (shouldCollapse) {
        const first = items[0];
        const last = items[items.length - 1];
        const hidden = items.slice(1, items.length - 1);

        const openOverflow = (event) => {
            const anchorEl = event.currentTarget;
            const options = hidden.map((it, i) => ({
                id: `breadcrumb-hidden-${i}`,
                label: it.label,
                icon: it.icon,
                onClick: () => {
                    if (it.onClick) it.onClick();
                    else if (it.href) window.location.assign(it.href);
                },
            }));
            popperRef.current?.open(options, 'bottom-start', anchorEl);
        };

        const openOverflowKeyDown = (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openOverflow(event);
            }
        };

        return (
            <nav
                aria-label={t('breadcrumb.label')}
                className={`breadcrumb ${className}`.trim()}
                {...props}
            >
                <ol className="breadcrumb__list">
                    <li className="breadcrumb__item">
                        <Crumb item={first} isCurrent={isCurrent(0)} />
                    </li>

                    <li className="breadcrumb__item" aria-hidden="true">
                        <Separator separator={separator} />
                    </li>

                    <li className="breadcrumb__item">
                        <span
                            role="button"
                            tabIndex={0}
                            aria-label={t('breadcrumb.showHidden', { count: hidden.length })}
                            className="breadcrumb__ellipsis"
                            onClick={openOverflow}
                            onKeyDown={openOverflowKeyDown}
                        >
                            <IconDots size={16} stroke={2} />
                        </span>
                    </li>

                    <li className="breadcrumb__item" aria-hidden="true">
                        <Separator separator={separator} />
                    </li>

                    <li className="breadcrumb__item">
                        <Crumb item={last} isCurrent={isCurrent(items.length - 1)} />
                    </li>
                </ol>

                <Popper controlRef={popperRef} />
            </nav>
        );
    }

    // ── Full trail ────────────────────────────────────────────────────────────
    return (
        <nav
            aria-label={t('breadcrumb.label')}
            className={`breadcrumb ${className}`.trim()}
            {...props}
        >
            <ol className="breadcrumb__list">
                {items.map((item, index) => (
                    <React.Fragment key={index}>
                        <li className="breadcrumb__item">
                            <Crumb item={item} isCurrent={isCurrent(index)} />
                        </li>
                        {index < items.length - 1 && (
                            <li className="breadcrumb__item" aria-hidden="true">
                                <Separator separator={separator} />
                            </li>
                        )}
                    </React.Fragment>
                ))}
            </ol>
        </nav>
    );
}
